import EventEmitter from "events";
import { AlbumDto, MediaDto, TenantDto, UserDto } from "../api/models";
import { json } from "stream/consumers";
import { getService } from "./api";
import { AuthService, TenantService, UserService } from "../api/services";

class Session {
    user: UserDto | null = null;
    _tenant: TenantDto | null = null;
    events: EventEmitter;
    
    constructor() {
        this.updateUser();
        this.events = new EventEmitter();
    }

    get isAdmin() {
        return this.user?.role === 'admin';
    }

    get isOwner() {
        return this.isAdmin || this.user?.role === 'owner';
    }

    get isGuest() {
        return this.user?.role === 'guess';
    }

    get isMember() {
        return this.user?.role === 'member'
    }

    get tenantId() {
        return this.user?.tenantId;
    }

    get userId() {
        return this.user?.id;
    }

    get tenant() {
        if (this._tenant) return this._tenant;

        let jsonTenant = window.localStorage.getItem('tenant');
        if (jsonTenant) {
            this._tenant = JSON.parse(jsonTenant) as TenantDto;
            return this._tenant;
        }
        return null;
    }

    setTenant(tenant: TenantDto | null, role?: string) {
        this._tenant = tenant;
        if (tenant) {
            window.localStorage.setItem('tenant', JSON.stringify(tenant));         
        } else {
            window.localStorage.removeItem('tenant');
        }
    }


    login(user: UserDto) {
        this.user = user;
        window.localStorage.setItem('user', JSON.stringify(user));
        this.events.emit('login', user);
    }

    logout() {
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('refresh-token');
        window.localStorage.removeItem('user');
        this.user = null;
        this.events.emit('logout');
    }

    updateUser() {
        this.user = JSON.parse(window.localStorage.getItem('user') || 'null');
        return this.user;
    }

    onUserChanged(user: UserDto) {
        this.user = user;
        window.localStorage.setItem('user', JSON.stringify(user));
        this.events.emit('onChanged', user);
    }

    getToken() {
        return localStorage.getItem('token');
    }

    getRefreshToken() {
        return localStorage.getItem('refresh-token');
    }

    canWriteAlbum(album: AlbumDto) {
        if (this.isGuest) return false;
        if (this.isMember && this.userId != album.userId) return false;
        return true;
    }

    canWriteMedia(media: MediaDto | undefined) {
        if (!media) return false;
        if (this.isGuest) return false;
        if (this.isMember && this.userId != media.userId) return false;
        return true;
    }

    canCreateMedia() {
        if (this.isGuest) return false;
        return true;
    }

    canCreateAlbum() {
        if (this.isGuest) return false;
        return true;
    }

    async reloadSession() {
        await this.loadToken(); // refresh token
        await this.loadUser(); // load user
    }

    async loadToken() {
        const srv = getService(AuthService);
        try {
            let reply = await srv.getToken({
                grandType: 'refresh_token',
                refreshToken: this.getRefreshToken()
            });
            if (reply) {
                if (reply.access_token) {
                    window.localStorage.setItem('token', reply.access_token);
                }
                if (reply.refresh_token) {
                    window.localStorage.setItem('refresh-token', reply.refresh_token);
                }
            }

            return reply.access_token;
        } catch (e) {
            console.error(e);
            window.location.assign("/login");
            throw new Error("Login Error");
        }
    }

    async loadUser() {
        try {
            const srv = getService(UserService);
            let reply = await srv.getSessionUser();           
            let tenant = await getService(TenantService).getSessionTenant();
            session.setTenant(tenant);
            session.onUserChanged(reply);
        } catch (e) {
            console.error(e);
            window.location.assign("/login");
            throw new Error("Login Error");
        }
    }

}

const session = new Session();

export default session;